<template>
  <!-- Settings sub-bar. Pickers edit the selected element (or set placement
       defaults) via two-way bound models owned by the parent. -->
  <div class="tool-subbar">
    <span v-if="context === 'Select'" class="toolbar-hint">Click to select, drag to move</span>

    <!-- Zone: square color swatches (rounded = area), name above-left -->
    <template v-if="context === 'Zone'">
      <div class="picker-stack">
        <span class="picker-current-name">{{ zoneBtnLabel(zoneType) }}</span>
        <div class="circle-picker">
          <StSwatch
            v-for="t in allZoneTypes"
            :key="t"
            shape="square"
            :color="zoneSwatchColor(t)"
            text-color="var(--st-white)"
            :label="zoneBtnLabel(t)"
            :selected="zoneType === t"
            @click="$emit('update:zoneType', t)"
          >
            {{ zoneBtnLabel(t).charAt(0) }}
          </StSwatch>
        </div>
      </div>
    </template>

    <!-- Objective: scrollable circle picker (color + symbol), name above-left -->
    <template v-if="context === 'Objective'">
      <div class="picker-stack">
        <span class="picker-current-name">{{ objectiveTypeName(objType) }}</span>
        <div class="circle-picker">
          <StSwatch
            v-for="t in allObjectiveTypes"
            :key="t"
            :color="OBJ_SWATCH[t].bg"
            :text-color="OBJ_SWATCH[t].fg"
            :border-color="OBJ_SWATCH[t].border"
            :icon="objectiveIconName(t) ?? undefined"
            :label="objectiveTypeName(t)"
            :selected="objType === t"
            @click="$emit('update:objType', t)"
          >
            <template v-if="!objectiveIconName(t)">{{ objectiveDisplayLabel(t) }}</template>
          </StSwatch>
        </div>
      </div>
    </template>

    <!-- Terrain: icon-circle type picker (name above-left) + size picker -->
    <template v-if="context === 'Terrain'">
      <div class="picker-stack">
        <span class="picker-current-name">{{ terrainTypeName(terrainType) }}</span>
        <div class="circle-picker">
          <StSwatch
            v-for="t in allTerrainTypes"
            :key="t"
            :icon="terrainIconName(t)"
            :label="terrainTypeName(t)"
            :selected="terrainType === t"
            @click="$emit('update:terrainType', t)"
          />
        </div>
      </div>
      <div class="picker-stack">
        <span class="picker-current-name">{{ sizeName(terrainSize) }}</span>
        <div class="circle-picker">
          <StSwatch
            v-for="sz in allTerrainSizes"
            :key="sz"
            :label="sizeName(sz)"
            :selected="terrainSize === sz"
            @click="$emit('update:terrainSize', sz)"
          >
            <span
              class="size-dot"
              :style="{ width: sizeDotPx(sz) + 'px', height: sizeDotPx(sz) + 'px' }"
            ></span>
          </StSwatch>
        </div>
      </div>
    </template>

    <!-- Icon: glyph picker (name above-left). The chosen icon name is freeform;
         these are just the curated presets. -->
    <template v-if="context === 'Icon'">
      <div class="picker-stack">
        <span class="picker-current-name">{{ iconOptionLabel(iconName) }}</span>
        <div class="circle-picker">
          <StSwatch
            v-for="opt in ICON_OPTIONS"
            :key="opt.name"
            :icon="opt.name"
            :label="opt.label"
            :selected="iconName === opt.name"
            @click="$emit('update:iconName', opt.name)"
          />
        </div>
      </div>
    </template>

    <!-- Mirror (objective + terrain): mirrors the element across an axis -->
    <div v-if="context === 'Objective' || context === 'Terrain'" class="picker-stack">
      <span class="picker-current-name">{{ mirrorName(mirror) }}</span>
      <div class="circle-picker">
        <StIconButton
          v-for="m in MIRROR_OPTIONS"
          :key="m.val"
          :icon="m.icon"
          :label="m.name"
          :active="mirror === m.val"
          @click="$emit('update:mirror', m.val)"
        />
      </div>
    </div>

    <!-- Placement hints only while placing (nothing selected) -->
    <span v-if="!selected && context === 'Label'" class="toolbar-hint">Click to place a label</span>
    <span v-if="!selected && context === 'Icon'" class="toolbar-hint">Click to place an icon</span>
    <span v-if="!selected && context === 'Arrow'" class="toolbar-hint">
      {{ arrowFirstClick ? 'Click end point' : 'Click start point' }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ObjectiveType, TerrainType, TerrainSize, ZoneType, type Mirror } from '@scrolldex/shared';
import {
  type ToolKind,
  MIRROR_OPTIONS,
  OBJ_SWATCH,
  ICON_OPTIONS,
  mirrorName,
  objectiveTypeName,
  objectiveDisplayLabel,
  objectiveIconName,
  zoneBtnLabel,
  terrainIconName,
  terrainTypeName,
  sizeDotPx,
  sizeName,
} from './labels.js';
import { StIconButton, StSwatch } from '../ui/index.js';

defineProps<{
  context: ToolKind;
  selected: boolean;
  arrowFirstClick: boolean;
  objType: ObjectiveType;
  terrainType: TerrainType;
  terrainSize: TerrainSize;
  zoneType: ZoneType;
  mirror: Mirror | 'none';
  iconName: string;
}>();

defineEmits<{
  'update:objType': [v: ObjectiveType];
  'update:terrainType': [v: TerrainType];
  'update:terrainSize': [v: TerrainSize];
  'update:zoneType': [v: ZoneType];
  'update:mirror': [v: Mirror | 'none'];
  'update:iconName': [v: string];
}>();

function iconOptionLabel(name: string): string {
  return ICON_OPTIONS.find((o) => o.name === name)?.label ?? name;
}

const allObjectiveTypes = Object.values(ObjectiveType);
const allTerrainTypes = Object.values(TerrainType);
const allTerrainSizes = Object.values(TerrainSize);
const allZoneTypes = Object.values(ZoneType);

function zoneSwatchColor(t: ZoneType): string {
  switch (t) {
    case ZoneType.Attacker:
      return '#c83c3c';
    case ZoneType.Defender:
      return '#3c6ec8';
    case ZoneType.Other:
      return '#9ca3af';
  }
}
</script>

<style scoped>
.tool-subbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--st-space-3);
  padding: var(--st-space-2) var(--st-space-3);
  /* Constant height across every tool. box-sizing is border-box (see base.css),
     so min-height must clear the tallest content (a captioned 34px picker ≈ 69px)
     PLUS this padding + top border, or the taller pickers push the bar down. */
  min-height: 5.75rem;
  background: var(--st-surface-sunken);
  border-top: 1px solid var(--st-border);
  font-size: var(--st-text-sm);
}

.toolbar-hint {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  font-style: italic;
}

.picker-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  min-width: 0;
}

.circle-picker {
  display: flex;
  align-items: center;
  gap: var(--st-space-2);
  max-width: 100%;
  overflow-x: auto;
  padding: 6px;
}

.picker-current-name {
  margin-bottom: 4px;
  padding-left: 6px;
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-semibold);
  color: var(--st-text-muted);
  white-space: nowrap;
}

.size-dot {
  display: block;
  border-radius: 50%;
  background: var(--st-text-subtle);
}
</style>
